import * as React from "react";
import type { SearchOption } from "../components/Map/TransitOverlay/MapSearchInput";
import type { LineDetails } from "../types";
import {
  getAllLinesGroupedByMunicipality,
  fetchDetailsForLines,
  type TrafficOverviewLine,
} from "../api/trafficLines";

const TRAFFIC_TYPE_LABELS: Record<string, string> = {
  TÄTO: "Tätort",
  LAND: "Landsbygd",
  EXPRESS: "Express",
  TÅG: "Tåg",
  UNKNOWN: "Övrigt",
};

type Bucket = {
  id: string;
  municipality: string;
  trafficGroupKey: string;
  trafficGroupLabel: string;
  lines: TrafficOverviewLine[];
};

export type TrafficOverviewSearchOption = SearchOption & {
  kind: "lineNetwork";
  trafficGroupKey: string;
  trafficGroupLabel: string;
  municipality?: string;
  lines: TrafficOverviewLine[];
};

function getTrafficTypeLabel(key: string, rawLabel?: string) {
  return TRAFFIC_TYPE_LABELS[key] ?? rawLabel ?? key;
}

function uniqLines(lines: TrafficOverviewLine[]) {
  return Array.from(new Map(lines.map((line) => [line.id, line])).values());
}

function normalize(value: string) {
  return value.trim().toLowerCase();
}

function lineCountLabel(count: number) {
  return count === 1 ? "1 linje" : `${count} linjer`;
}

let trafficOverviewItemsCache: TrafficOverviewLine[] | null = null;
let trafficOverviewItemsPromise: Promise<TrafficOverviewLine[]> | null = null;
export function useTrafficOverviewSearch(query: string) {
  const [items, setItems] = React.useState<TrafficOverviewLine[] | null>(
    trafficOverviewItemsCache
  );
  const [loading, setLoading] = React.useState(false);
  const [detailsLoading, setDetailsLoading] = React.useState(false);

  React.useEffect(() => {
    let mounted = true;

    if (query.trim().length < 1) return;
    if (trafficOverviewItemsCache?.length) {
      setItems(trafficOverviewItemsCache);
      return;
    }

    setLoading(true);

    if (!trafficOverviewItemsPromise) {
      trafficOverviewItemsPromise = getAllLinesGroupedByMunicipality();
    }

    trafficOverviewItemsPromise
      .then((result) => {
        trafficOverviewItemsCache = result;

        if (!mounted) return;
        setItems(result);
      })
      .catch((err) => {
        console.error("Kunde inte hämta linjenätet", err);

        if (!trafficOverviewItemsCache) {
          trafficOverviewItemsPromise = null;
        }
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [query]);

  const buckets = React.useMemo<Bucket[]>(() => {
    const safeItems = items ?? [];
    const map = new Map<string, Bucket>();

    for (const line of safeItems) {
      const groupKey = line.trafficGroupKey || "UNKNOWN";
      const bucketId = `${line.municipality}__${groupKey}`;

      if (!map.has(bucketId)) {
        const rawLabel = line.trafficGroupLabel || groupKey;

        map.set(bucketId, {
          id: bucketId,
          municipality: line.municipality,
          trafficGroupKey: groupKey,
          trafficGroupLabel: getTrafficTypeLabel(groupKey, rawLabel),
          lines: [],
        });
      }

      map.get(bucketId)!.lines.push(line);
    }

    return Array.from(map.values()).sort((a, b) => {
      const g = a.trafficGroupLabel.localeCompare(b.trafficGroupLabel, "sv");
      if (g !== 0) return g;
      return a.municipality.localeCompare(b.municipality, "sv");
    });
  }, [items]);

  const options = React.useMemo<TrafficOverviewSearchOption[]>(() => {
    const q = normalize(query);
    if (!q) return [];

    const byGroup = new Map<
      string,
      {
        trafficGroupKey: string;
        trafficGroupLabel: string;
        lines: TrafficOverviewLine[];
      }
    >();

    for (const bucket of buckets) {
      if (!byGroup.has(bucket.trafficGroupKey)) {
        byGroup.set(bucket.trafficGroupKey, {
          trafficGroupKey: bucket.trafficGroupKey,
          trafficGroupLabel: bucket.trafficGroupLabel,
          lines: [],
        });
      }

      byGroup.get(bucket.trafficGroupKey)!.lines.push(...bucket.lines);
    }

    const groupOptions: TrafficOverviewSearchOption[] = Array.from(
      byGroup.values()
    ).map((group) => {
      const lines = uniqLines(group.lines);

      return {
        id: `lineNetwork:group:${group.trafficGroupKey}`,
        kind: "lineNetwork",
        type: "Linjenät",
        label: `All ${group.trafficGroupLabel} (${lineCountLabel(
          lines.length
        )})`,
        value: `${group.trafficGroupLabel} ${lines
          .map((line) => line.displayName)
          .join(" ")}`,
        trafficGroupKey: group.trafficGroupKey,
        trafficGroupLabel: group.trafficGroupLabel,
        lines,
      };
    });

    const bucketOptions: TrafficOverviewSearchOption[] = buckets.map(
      (bucket) => {
        const lines = uniqLines(bucket.lines);

        return {
          id: `lineNetwork:bucket:${bucket.id}`,
          kind: "lineNetwork",
          type: "Område",
          label: `${bucket.municipality}, ${
            bucket.trafficGroupLabel
          } (${lineCountLabel(lines.length)}) `,
          value: `${bucket.municipality} ${bucket.trafficGroupLabel} ${lines
            .map((line) => line.displayName)
            .join(" ")}`,
          trafficGroupKey: bucket.trafficGroupKey,
          trafficGroupLabel: bucket.trafficGroupLabel,
          municipality: bucket.municipality,
          lines,
        };
      }
    );

    return [...groupOptions, ...bucketOptions]
      .filter((option) =>
        normalize(`${option.label} ${option.value}`).includes(q)
      )
      .sort((a, b) => {
        const aLabel = normalize(a.label);
        const bLabel = normalize(b.label);

        const aStarts = aLabel.startsWith(q) ? 1 : 0;
        const bStarts = bLabel.startsWith(q) ? 1 : 0;

        if (aStarts !== bStarts) return bStarts - aStarts;

        const aIsNetwork = a.type === "Linjenät" ? 1 : 0;
        const bIsNetwork = b.type === "Linjenät" ? 1 : 0;

        if (aIsNetwork !== bIsNetwork) return bIsNetwork - aIsNetwork;

        return a.label.localeCompare(b.label, "sv");
      })
      .slice(0, 12);
  }, [query, buckets]);

  const fetchDetailsForOption = React.useCallback(
    async (option: TrafficOverviewSearchOption): Promise<LineDetails[]> => {
      setDetailsLoading(true);

      try {
        return await fetchDetailsForLines(option.lines);
      } finally {
        setDetailsLoading(false);
      }
    },
    []
  );

  return {
    options,
    loading: loading || detailsLoading,
    fetchDetailsForOption,
  };
}
